import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
// Import Store
import { useAppSelector } from 'store';
import { selectPageValidation } from 'store/slices/pageValidation';
// Import Components
import { Dropdown, Checkbox, Button, Icon, Tooltip } from '@geneui/components';
import UploadFile from 'components/common/UploadFile';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Constants
import { l } from 'constants/common';

const StaticSegmentGeneralDetails = ({
    clientFields,
    clientFieldId,
    fileName,
    handleFormSubmit,
    handleIsIgnoreInvalidsChange,
    handleIsIncludeDuplicatesChange,
    handleClientFieldsChange,
    handleFileInputChange,
    isDisableUploadBtn,
    isDisableCheckBoxes,
    isDisableFormFields,
    fileRef,
}) => {
    const { t } = useTranslation();
    const { fileName: fileNameValidation } = useAppSelector(selectPageValidation);

    const formSubmitHandler = (e) => {
        e.preventDefault();
        handleFormSubmit(e);
    };

    const fileInputChangeHandler = (e) => {
        handleFileInputChange(e);
    };

    const clientFieldsChangeHandler = (e) => {
        handleClientFieldsChange(e);
    };

    const isIgnoreInvalidsChangeHandler = (e) => {
        handleIsIgnoreInvalidsChange(e);
    };

    const isIncludeDuplicatesChangeHandler = (e) => {
        handleIsIncludeDuplicatesChange(e);
    };

    return (
        <SectionWithTitle title={t(l.GeneralDetails)} hasShadow={true} hasContentPadding={false}>
            <form onSubmit={formSubmitHandler} className="form-row-inner">
                <div className="crm-gen-details-uploader">
                    <UploadFile
                        ref={fileRef}
                        onChange={fileInputChangeHandler}
                        value={fileName}
                        disabled={isDisableFormFields}
                        errorText={fileNameValidation?.errorText}
                    />
                </div>
                <Tooltip text={t(l.TooltipStaticSegmentUploadFile)} position="bottom">
                    <Icon type="bc-icon-info" />
                </Tooltip>
                <Dropdown
                    disabled={isDisableFormFields}
                    appearance="outline"
                    noDataWithImage
                    data={clientFields}
                    noDataText={t(l.NoDataFound)}
                    labelAppearance="swap"
                    placeholder={t(l.ClientField)}
                    onChange={clientFieldsChangeHandler}
                    value={clientFieldId}
                    hasSearch={false}
                />
                <Tooltip text={t(l.TooltipClientField)} position="bottom">
                    <Icon type="bc-icon-info" onClick={noop} />
                </Tooltip>
                <Button
                    type="submit"
                    className="crm-gen-details-submit"
                    flexibility="content-size"
                    disabled={isDisableUploadBtn}
                >
                    {t(l.UploadFile)}
                </Button>
                {!isDisableCheckBoxes && (
                    <div className="crm-gen-details-check-group">
                        <Checkbox label={t(l.IgnoreInvalids)} onChange={isIgnoreInvalidsChangeHandler} />
                        <Tooltip text={t(l.TooltipIgnoreInvalids)} position="bottom">
                            <Icon type="bc-icon-info" />
                        </Tooltip>
                        <Checkbox label={t(l.IncludeDuplicates)} onChange={isIncludeDuplicatesChangeHandler} />
                        <Tooltip text={t(l.TooltipIncludeDuplicates)} position="bottom">
                            <Icon type="bc-icon-info" />
                        </Tooltip>
                    </div>
                )}
            </form>
        </SectionWithTitle>
    );
};

StaticSegmentGeneralDetails.propTypes = {
    clientFields: PropTypes.array,
    clientFieldId: PropTypes.string,
    fileName: PropTypes.string,
    isDisableUploadBtn: PropTypes.bool,
    isDisableCheckBoxes: PropTypes.bool,
    isDisableFormFields: PropTypes.bool,
    handleFormSubmit: PropTypes.func,
    handleIsIgnoreInvalidsChange: PropTypes.func,
    handleIsIncludeDuplicatesChange: PropTypes.func,
    handleClientFieldsChange: PropTypes.func,
    handleFileInputChange: PropTypes.func,
    fileRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
};

StaticSegmentGeneralDetails.defaultProps = {
    clientFields: null,
    isDisableUploadBtn: false,
    isDisableCheckBoxes: false,
    isDisableFormFields: false,
    handleFormSubmit: noop,
    handleIsIgnoreInvalidsChange: noop,
    handleIsIncludeDuplicatesChange: noop,
    handleClientFieldsChange: noop,
    handleFileInputChange: noop,
    fileRef: noop,
};
export default memo(StaticSegmentGeneralDetails);
